// Client for the transcript Worker, cached in localStorage per device.

// Exported so tests use the real key across version bumps.
export const CACHE_KEY = 'ezshadowTranscripts.v2';
export const MAX_CACHE_ENTRIES = 12;

// The cache has no TTL: bump when a cached segments shape stops matching
// current code (pairs with the Worker's TRANSLATE_CACHE_VERSION).
const TRANSLATE_CLIENT_CACHE_VERSION = 4;

// Bump together with the Worker's REFINE_CACHE_VERSION.
const REFINE_CLIENT_CACHE_VERSION = 2;

export function TranscriptError(code, message) {
  const err = new Error(message || code);
  err.name = 'TranscriptError';
  err.code = code; // 'NO_TRANSCRIPT' | 'UPSTREAM_BLOCKED' | 'NETWORK'
  return err;
}

// Dedupe concurrent requests: LearningScreen fires several on mount, and racing
// fetches could settle differently for the same video.
const inFlight = new Map();

// opts.translate: Worker translates FROM opts.from (default "en") TO lang.
// opts.refine: punctuation-restored tier (no-op on manual tracks).
// Rejects with .code NO_TRANSCRIPT | UPSTREAM_BLOCKED | NETWORK.
export function getTranscript(videoId, lang, opts) {
  const translate = !!(opts && opts.translate);
  const refine = !translate && !!(opts && opts.refine);
  const from = (opts && opts.from) || 'en';
  let cacheId = `${videoId}:${lang}`;
  if (translate) cacheId = `${videoId}:${lang}:from${from}:t${TRANSLATE_CLIENT_CACHE_VERSION}`;
  else if (refine) cacheId = `${videoId}:${lang}:refined:r${REFINE_CLIENT_CACHE_VERSION}`;

  const cache = loadCache();
  const hit = cache.entries[cacheId];
  if (hit) {
    hit.lastUsedAt = Date.now();
    saveCache(cache);
    return Promise.resolve(hit.data);
  }

  if (inFlight.has(cacheId)) return inFlight.get(cacheId);

  const request = fetchTranscript(videoId, lang, cacheId, { translate, refine, from });
  inFlight.set(cacheId, request);
  request.then(
    () => inFlight.delete(cacheId),
    () => inFlight.delete(cacheId)
  );
  return request;
}

// YouTube bot-checks some Worker egress IPs: ~42% of fresh requests on a new
// clip, in streaks of up to 3, measured 2026-09-26. The Worker fails those fast
// (~1.5 s) and a new request usually gets a clean IP, so retry here: 7 tries
// is ~0.2% failure, ~20 s worst case. The 12 s stall banner may show before
// the last tries, but it doesn't stop the load (BUGFIX-009, BUGFIX-012).
// A timed-out attempt already cost 20 s, so timeouts get one retry only.
const BLOCKED_RETRY_DELAYS_MS = [500, 1000, 1500, 2000, 2500, 3000];
const TIMEOUT_RETRIES = 1;

async function fetchTranscript(videoId, lang, cacheId, opts) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchTranscriptOnce(videoId, lang, cacheId, opts);
    } catch (e) {
      // NO_TRANSCRIPT is a real answer; NETWORK usually means offline.
      if (e.code !== 'UPSTREAM_BLOCKED' || attempt >= BLOCKED_RETRY_DELAYS_MS.length) throw e;
      if (e.timedOut && attempt >= TIMEOUT_RETRIES) throw e;
      await new Promise(resolve => setTimeout(resolve, BLOCKED_RETRY_DELAYS_MS[attempt]));
    }
  }
}

// Not AbortSignal.timeout(): Safari 16.4+ only.
const CLIENT_FETCH_TIMEOUT_MS = 20000;

async function fetchTranscriptOnce(videoId, lang, cacheId, { translate, refine, from }) {
  const apiUrl = process.env.TRANSCRIPT_API_URL;
  if (!apiUrl) {
    throw TranscriptError('NETWORK', 'TRANSCRIPT_API_URL is not configured');
  }

  let response;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_FETCH_TIMEOUT_MS);
  try {
    let url = `${apiUrl}/v1/transcript?videoId=${encodeURIComponent(videoId)}&lang=${encodeURIComponent(lang)}&wv=2`;
    if (translate) url += `&translate=1&from=${encodeURIComponent(from)}`;
    else if (refine) url += '&refine=1';
    response = await fetch(url, { signal: controller.signal });
  } catch (e) {
    // A timeout is an upstream stall (retryable); real offline stays NETWORK.
    if (e && e.name === 'AbortError') {
      const err = TranscriptError('UPSTREAM_BLOCKED', 'transcript fetch timed out');
      err.timedOut = true;
      throw err;
    }
    throw TranscriptError('NETWORK', e && e.message);
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let code = 'NETWORK';
    try {
      const body = await response.json();
      if (body.error === 'NO_TRANSCRIPT') code = 'NO_TRANSCRIPT';
      else if (body.error === 'UPSTREAM_BLOCKED') code = 'UPSTREAM_BLOCKED';
    } catch (e) { /* non-JSON error body */ }
    throw TranscriptError(code, `transcript API responded ${response.status}`);
  }

  const body = await response.json();
  const data = {
    segments: body.segments || [],
    isGenerated: !!body.isGenerated,
    refined: !!body.refined,
    trackLang: body.trackLang || lang,
    translationSource: body.translationSource || null
  };

  // Refinement runs in the background; don't pin the unrefined version in the permanent cache.
  const awaitingRefinement = refine && !data.refined && data.isGenerated;
  if (!awaitingRefinement) {
    const cache = loadCache();
    cache.entries[cacheId] = { lastUsedAt: Date.now(), data };
    evictOverflow(cache);
    saveCache(cache);
  }

  return data;
}

// Caption tracks rarely share boundaries and auto cues overlap neighbours by
// seconds, so a segment counts only if most of its own span is in the window.
const OVERLAP_FRACTION = 0.4;

export function getSegmentText(segments, startSec, endSec, pad = 0.25) {
  const windowStart = startSec - pad;
  const windowEnd = endSec + pad;
  return segments
    .filter(seg => {
      const segStart = seg.start;
      const segEnd = seg.start + (seg.dur || 0);
      const overlap = Math.min(segEnd, windowEnd) - Math.max(segStart, windowStart);
      if (overlap <= 0) return false;
      const segDur = segEnd - segStart;
      return segDur <= 0 ? true : overlap / segDur >= OVERLAP_FRACTION;
    })
    .map(seg => seg.text)
    .join(' ')
    .trim();
}

export function clearTranscriptCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (e) { /* storage unavailable */ }
}

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.version === 1 && parsed.entries) {
        return parsed;
      }
    }
  } catch (e) { /* corrupt or unavailable -> start fresh */ }
  return { version: 1, entries: {} };
}

function saveCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    const ids = sortedByOldest(cache);
    ids.slice(0, Math.ceil(ids.length / 2)).forEach(id => {
      delete cache.entries[id];
    });
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e2) { /* give up; cache stays in memory only */ }
  }
}

function evictOverflow(cache) {
  const ids = sortedByOldest(cache);
  while (ids.length > MAX_CACHE_ENTRIES) {
    delete cache.entries[ids.shift()];
  }
}

function sortedByOldest(cache) {
  return Object.keys(cache.entries).sort(
    (a, b) => cache.entries[a].lastUsedAt - cache.entries[b].lastUsedAt
  );
}
